/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { CarouselProvider } from '@/components/blocks/Carousel/CarouselProvider';
import { CarouselSlide } from '@/components/blocks/Carousel/CarouselSlide';
import { CarouselProps } from '@/components/blocks/ProductDetails/Carousel';
import { productDetailsThumbnailSliderSX } from '@/components/blocks/ProductDetails/styles/thumbnailSlider';
import { ProductImage } from '@/components/blocks/ProductImage';
import { ArrowForwardIos, ArrowBackIosNew } from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ButtonBack, ButtonNext, DotGroup, Slider } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { FC, useMemo } from 'react';
import { useDeviceType } from '@/data/deviceDetection';
import { useLocalization } from '@/data/Localization';

const MD_UP_DIMS = { slideWidth: 12, slideHeight: 15, visibleSlides: 4 };
const SM_DIMS = { slideWidth: 5, slideHeight: 3, visibleSlides: 6 };
const XS_DIMS = { slideWidth: 1, slideHeight: 1, visibleSlides: 6 };

export const ProductDetailsThumbnailCarousel: FC<CarouselProps> = ({ slides, choose, index }) => {
	const theme = useTheme();
	const isSm = useMediaQuery(theme.breakpoints.down('md'));
	const isXs = useMediaQuery(theme.breakpoints.down('sm'));
	const { isDesktop } = useDeviceType();
	const isThumbnail = !isSm;
	const { slideWidth, slideHeight, visibleSlides } = isXs ? XS_DIMS : isSm ? SM_DIMS : MD_UP_DIMS;
	const showButtons = useMemo(() => (slides.length > 1 && isDesktop), [slides, visibleSlides, isDesktop]);
	const commonNLS = useLocalization("Common");

	const handleButtonClick = (action:string) => {
		if(index!==undefined){
			if (action === "NEXT") {
				choose((index+1)===slides.length ? 0 : (index+1));
			} else {
				choose(index === 0 ? (slides.length-1) : (index - 1));
			}
		}
	}
	
	return (
		<CarouselProvider
			naturalSlideWidth={slideWidth}
			naturalSlideHeight={slideHeight}
			visibleSlides={visibleSlides}
			step={1}
			infinite={true}
			dragEnabled={isSm}
			totalSlides={slides.length}
			orientation="horizontal"
			sx={productDetailsThumbnailSliderSX}
			{...(!isSm && { currentSlide: index })}
		>
			{showButtons && !isSm ? (
				<ButtonBack onClick={()=>handleButtonClick("PREV")} aria-label={commonNLS.SlidePreviousARIA.t()}>
					<ArrowBackIosNew sx={{fontSize:18}} />
				</ButtonBack>
			) : null}

			<Slider>
				{slides.map(({ thumbnail }, i) => {
					thumbnail = thumbnail?.replaceAll('hclstore', 'wcsstore').replace('wcsstore/wcsstore', 'wcsstore');
					return(
						<CarouselSlide index={i} key={`${isSm}-${i}`} onClick={() => choose(i)} >
							<ProductImage
								src={thumbnail}
								alt={i===0?slides[0].name?.replace('Main Image','hoofdafbeelding product'):slides[0].name?.replace('Main Image','productafbeelding')+' '+(i+1)}
								isThumbnail={isThumbnail}
								isSelected={i === index}
								calledFrom = "CarouselSlide"
							/>
						</CarouselSlide>
					)
				})}
			</Slider>

			{isSm ? (
				<>
					
					{showButtons ? (
						<>
							<ButtonBack onClick={()=>handleButtonClick("PREV")} aria-label={commonNLS.SlidePreviousARIA.t()}>
								<ArrowBackIosNew sx={{fontSize:18}} />
							</ButtonBack>
							<ButtonNext onClick={()=>handleButtonClick("NEXT")} aria-label={commonNLS.SlideNextARIA.t()}>
								<ArrowForwardIos sx={{fontSize:18}} />
							</ButtonNext>
							{/* <DotGroup /> */}
						</>
					) : null}
				</>
			) : showButtons ? (
				<ButtonNext  onClick={()=>handleButtonClick("NEXT")} aria-label={commonNLS.SlideNextARIA.t()}>
					<ArrowForwardIos sx={{fontSize:18}} />
				</ButtonNext>
			) : null}
		</CarouselProvider>
	);
};
